/**
 * MODULE: _shared/lib/frontend/api/leadApi.ts
 * PURPOSE: Leads/Pipeline API client - manages sales pipeline leads via sales-accounts contacts
 *
 * DEPENDENCIES:
 *  - axios: HTTP client
 *  - ./config: API_CONFIG, getAuthHeaders
 *
 * PUBLIC API:
 *  - Lead: TypeScript interface
 *  - CreateLeadRequest: Interface for lead creation
 *  - leadApi.list(params): List leads with filters
 *  - leadApi.get(leadId): Get single lead
 *  - leadApi.create(accountId, lead): Create new lead
 *  - leadApi.update(leadId, updates): Update lead
 *  - leadApi.convertToOpportunity(leadId, data): Convert lead (sales-accounts)
 *  - leadApi.qualify(leadId, opportunityData): Qualify lead via Pipeline Generation API
 *  - leadApi.convertToOpportunityFull(leadId, opportunityData): Full qualify + fetch opportunity
 *
 * SIDE EFFECTS:
 *  - HTTP requests to sales-accounts and pipeline endpoints
 *  - Reads JWT token from localStorage
 *
 * AGENT SAFETY: RESTRICTED
 * RECENT: 2026-02-25 - Frontend-backend integration [2.2.135]
 * See: API.md | domain/sales/README.md
 */

import axios from 'axios';
import { API_CONFIG, getAuthHeaders } from './config';
import type { OpportunityWithProject } from './opportunityApi';
import { opportunityApi } from './opportunityApi';

const API_BASE = API_CONFIG.baseURL;

export interface Lead {
  id: string;
  tenant_id: string;
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  company?: string;
  job_title?: string;
  status: string;
  source?: string;
  score?: number;
  account_id?: string;
  created_at: string;
  updated_at: string;
}

export interface CreateLeadRequest {
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  company?: string;
  job_title?: string;
  status?: string;
  source?: string;
  account_id?: string;
}

export const leadApi = {
  list: async (params?: {
    account_id?: string;
    status?: string;
    search?: string;
    page?: number;
    per_page?: number;
  }): Promise<{ leads: Lead[]; total: number }> => {
    if (params?.account_id) {
      const response = await axios.get(
        `${API_BASE}/api/v1/sales-accounts/accounts/${params.account_id}/contacts`,
        {
          params: {
            page: params.page,
            per_page: params.per_page,
            search: params.search,
          },
          headers: getAuthHeaders(),
        }
      );
      const leads = (response.data.contacts ?? []).filter(
        (c: { source?: string }) => c.source !== 'crm'
      );
      return { leads, total: leads.length };
    }
    return { leads: [], total: 0 };
  },

  get: async (leadId: string): Promise<Lead> => {
    const response = await axios.get(
      `${API_BASE}/api/v1/sales-accounts/contacts/${leadId}`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  create: async (accountId: string, lead: CreateLeadRequest): Promise<Lead> => {
    const response = await axios.post(
      `${API_BASE}/api/v1/sales-accounts/accounts/${accountId}/contacts`,
      { ...lead, source: 'pipeline' },
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  update: async (
    leadId: string,
    updates: Partial<CreateLeadRequest>
  ): Promise<Lead> => {
    const response = await axios.patch(
      `${API_BASE}/api/v1/sales-accounts/contacts/${leadId}`,
      updates,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  convertToOpportunity: async (
    leadId: string,
    data: { name: string; value?: number; stage?: string }
  ): Promise<unknown> => {
    const response = await axios.patch(
      `${API_BASE}/api/v1/sales-accounts/contacts/${leadId}`,
      { status: 'converted', ...data },
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  /**
   * Qualify lead via Pipeline Generation API (/api/v1/leads)
   * Maps frontend fields to backend QualifyLeadRequest schema.
   * Returns: { lead: Lead, opportunity_id?: string }
   */
  qualify: async (
    leadId: string,
    opportunityData?: {
      name?: string;
      expected_value?: number;
      close_date?: string;
      stage?: string;
      notes?: string;
    }
  ): Promise<{ lead: Lead; opportunity_id?: string }> => {
    const payload: Record<string, unknown> = {};
    if (opportunityData?.name) payload.opportunity_name = opportunityData.name;
    if (opportunityData?.expected_value !== undefined) payload.opportunity_value = opportunityData.expected_value;
    if (opportunityData?.close_date) payload.expected_close_date = opportunityData.close_date;
    if (opportunityData?.stage) payload.stage = opportunityData.stage;
    if (opportunityData?.notes) payload.notes = opportunityData.notes;

    const response = await axios.post(
      `${API_BASE}/api/v1/leads/${leadId}/qualify`,
      payload,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  /**
   * Complete lead-to-opportunity conversion via Pipeline Generation
   * 1. Qualifies lead (creates opportunity in backend)
   * 2. Fetches full opportunity with project details
   */
  convertToOpportunityFull: async (
    leadId: string,
    opportunityData: {
      name: string;
      expected_value?: number;
      stage?: string;
      close_date?: string;
      notes?: string;
    }
  ): Promise<{ lead: Lead; opportunity: OpportunityWithProject }> => {
    const qualifyResult = await leadApi.qualify(leadId, opportunityData);

    if (!qualifyResult.opportunity_id) {
      throw new Error('Lead qualification did not create opportunity');
    }

    const opportunity = await opportunityApi.get(qualifyResult.opportunity_id);

    return {
      lead: qualifyResult.lead,
      opportunity,
    };
  },
};
